import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * AvatarUpdateBanner - Prompts users with legacy avatars to select a new one
 * 
 * Shows when:
 * - Company has new_avatars feature enabled
 * - User has a legacy avatar (doesn't match avatar_XXX.png pattern)
 */
const AvatarUpdateBanner = ({ 
  user, 
  companySettings = {}, 
  onUpdateClick,
  onDismiss 
}) => {
  const [dismissed, setDismissed] = useState(false);

  // Check if banner should be shown
  const shouldShowBanner = () => {
    if (dismissed) return false;
    if (!companySettings?.new_avatars_enabled) return false;
    
    const profilePicture = user?.profile_picture;
    if (!profilePicture) return true; // No avatar set, prompt to select
    
    const fileName = profilePicture.split('/').pop();
    // Check if it's a new-style avatar
    const isNewStyleAvatar = fileName?.match(/^avatar_\d{3}(\.png)?$/);
    return !isNewStyleAvatar; // Show banner if NOT a new-style avatar
  };

  if (!shouldShowBanner()) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  return (
    <div className="bg-succession-primary-10 border border-succession-primary rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg 
              className="w-6 h-6 text-succession-primary" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-succession-primary">
              We&apos;ve updated our avatars!
            </h3>
            <p className="mt-1 text-sm text-succession-text-secondary">
              Choose from our new collection of professional avatars to personalize your profile.
            </p>
            <button
              onClick={onUpdateClick}
              className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-succession-primary rounded-md hover:opacity-90 transition-opacity"
            >
              Select New Avatar
              <svg 
                className="ml-1.5 w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 hover:bg-succession-bg-secondary rounded transition-colors"
          aria-label="Dismiss"
        >
          <svg 
            className="w-5 h-5 text-succession-text-muted" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M6 18L18 6M6 6l12 12" 
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

AvatarUpdateBanner.propTypes = {
  /** Current user object */
  user: PropTypes.shape({
    profile_picture: PropTypes.string,
  }),
  /** Company settings with avatar configuration */
  companySettings: PropTypes.shape({
    new_avatars_enabled: PropTypes.bool,
  }),
  /** Callback when user clicks to update avatar */
  onUpdateClick: PropTypes.func.isRequired,
  /** Callback when banner is dismissed */
  onDismiss: PropTypes.func,
};

export default AvatarUpdateBanner;
